/* eslint-env vue/setup-compiler-macros */

<template>
  <BModal
    :model-value="isVisible"
    @update:modelValue="emit('update:modelValue', $event)"
    title="Location Map"
    size="lg"
    hide-footer
    @shown="initializeMap"
    @hidden="destroyMap"
  >
    <div ref="mapContainer" style="height: 500px; position: relative;">
      <div v-if="loading" class="text-center py-5">
        Loading map...
      </div>
      <div v-else-if="!hasValidCoordinates" class="text-muted text-center py-5">
        No valid location data available.
      </div>
    </div>
  </BModal>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';

// Google Maps will be available globally after loading
const GOOGLE_MAPS_API_KEY = 'AIzaSyDI0kI226YXV8qMA6JSdyS5Q17Vs4eUSEw'; // Replace with your actual API key

const DEFAULT_CENTER = { lat: 50.4108, lng: 4.4446 };

const props = defineProps({
  gps: { 
    type: Object, 
    default: () => null,
    validator: (value) => {
      if (!value) return true;
      return (
        typeof value.lat === 'number' && 
        typeof value.lng === 'number' &&
        value.lat >= -90 &&
        value.lat <= 90 &&
        value.lng >= -180 &&
        value.lng <= 180
      );
    }
  },
  modelValue: { type: Boolean, required: true }
});

const emit = defineEmits(['update:modelValue']);
const isVisible = computed(() => props.modelValue);

const mapContainer = ref(null);
const loading = ref(true);
const mapInitialized = ref(false);
const mapInstance = ref(null);
const marker = ref(null);

const hasValidCoordinates = computed(() => {
  return props.gps?.lat !== undefined && 
         props.gps?.lng !== undefined &&
         !isNaN(props.gps.lat) && 
         !isNaN(props.gps.lng);
});

const mapCenter = computed(() => {
  return hasValidCoordinates.value
    ? { lat: props.gps.lat, lng: props.gps.lng }
    : DEFAULT_CENTER;
});

const loadGoogleMaps = () => {
  return new Promise((resolve) => {
    if (window.google?.maps) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
};

const initializeMap = async () => {
  if (mapInitialized.value) return;
  
  loading.value = true;
  
  try {
    await loadGoogleMaps();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (!mapContainer.value) {
      console.error('Map container not available');
      loading.value = false;
      return;
    }

    mapInstance.value = new window.google.maps.Map(mapContainer.value, {
      center: mapCenter.value,
      zoom: 13,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP
    });

    if (hasValidCoordinates.value) {
      marker.value = new window.google.maps.Marker({
        position: mapCenter.value,
        map: mapInstance.value,
        title: 'Selected location'
      });
    }

    mapInitialized.value = true;
  } catch (error) {
    console.error('Error loading Google Maps:', error);
  } finally {
    loading.value = false;
  }
};

const updateMap = () => {
  if (!mapInstance.value || !window.google?.maps) return;
  
  mapInstance.value.setCenter(mapCenter.value);
  
  if (marker.value) {
    marker.value.setMap(null);
  }

  if (hasValidCoordinates.value) {
    marker.value = new window.google.maps.Marker({
      position: mapCenter.value,
      map: mapInstance.value,
      title: 'Selected location'
    });
  }
};

const destroyMap = () => {
  if (marker.value) {
    marker.value.setMap(null);
    marker.value = null;
  }
  mapInstance.value = null;
  mapInitialized.value = false;
  loading.value = true;
};

watch(() => props.gps, () => {
  if (mapInitialized.value && hasValidCoordinates.value) {
    updateMap();
  }
}, { deep: true });

onBeforeUnmount(() => {
  destroyMap();
});
</script>